/**
 * Delight Layer Type Definitions
 *
 * Types for the celebration/acknowledgement system that rewards positive user behaviors.
 */

/** Celebration types supported by the system */
export type CelebrationType =
  | 'weekly_update' // After sending weekly client update
  | 'health_boost' // When health status improves to green
  | 'sync_champion' // ClickUp push milestones (1, 25, 50, 100, 200...)
  | 'growth' // Adding client/contract/goal or connecting project
  | 'bid_submitted' // When a job bid/application is submitted
  | 'bid_won'; // When a job bid results in a win

/** Xolo dog animation states */
export type XoloState = 'idle' | 'wag' | 'celebrate';

/** Trigger sources for celebrations */
export type CelebrationTriggerSource =
  | 'client'
  | 'contract'
  | 'project'
  | 'goal'
  | 'obligation'
  | 'clickup_push'
  | 'weekly_email'
  | 'job_bid';

/** User delight preferences */
export interface DelightPreferences {
  /** Master toggle for all celebration animations */
  enableCelebrations: boolean;
  /** Celebrate when weekly updates are sent */
  celebrateWeeklyUpdates: boolean;
  /** Celebrate when health status improves to green */
  celebrateHealthBoosts: boolean;
  /** Celebrate ClickUp sync milestones */
  celebrateSyncMilestones: boolean;
  /** Celebrate portfolio growth (new clients, contracts, etc.) */
  celebrateGrowth: boolean;
  /** Celebrate when job bids/applications are submitted */
  celebrateBidSubmissions: boolean;
  /** Whether confetti animations are shown */
  enableConfetti: boolean;
  /** Future: sound effects */
  enableSound: boolean;
}

/** Default preferences */
export const DEFAULT_DELIGHT_PREFERENCES: DelightPreferences = {
  enableCelebrations: true,
  celebrateWeeklyUpdates: true,
  celebrateHealthBoosts: true,
  celebrateSyncMilestones: true,
  celebrateGrowth: true,
  celebrateBidSubmissions: true,
  enableConfetti: true,
  enableSound: false,
};

/** Delight event payload */
export interface DelightEvent {
  type: CelebrationType;
  message: string;
  subtitle?: string;
  metadata?: Record<string, unknown>;
}

/** Options for triggering a celebration */
export interface DelightTriggerOptions {
  /** Skip checking user preferences */
  skipPreferenceCheck?: boolean;
  /** Force confetti even for non-milestone events */
  forceConfetti?: boolean;
  /** Custom toast duration in ms */
  duration?: number;
}

/** Celebration configuration for each type */
export interface CelebrationConfig {
  title: string;
  icon: string;
  xoloState: XoloState;
  bgColor: string;
  showConfetti: boolean;
}

/** Celebration configs by type */
export const CELEBRATION_CONFIGS: Record<CelebrationType, CelebrationConfig> = {
  weekly_update: {
    title: 'Update Sent!',
    icon: '📧',
    xoloState: 'wag',
    bgColor: 'bg-blue-50 dark:bg-blue-950',
    showConfetti: false,
  },
  health_boost: {
    title: 'Health Improved!',
    icon: '💚',
    xoloState: 'celebrate',
    bgColor: 'bg-green-50 dark:bg-green-950',
    showConfetti: true,
  },
  sync_champion: {
    title: 'Sync Champion!',
    icon: '🎯',
    xoloState: 'celebrate',
    bgColor: 'bg-purple-50 dark:bg-purple-950',
    showConfetti: false, // Set dynamically based on milestone
  },
  growth: {
    title: 'Growing!',
    icon: '🌱',
    xoloState: 'wag',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950',
    showConfetti: false,
  },
  bid_submitted: {
    title: 'Application Sent!',
    icon: '🚀',
    xoloState: 'celebrate',
    bgColor: 'bg-amber-50 dark:bg-amber-950',
    showConfetti: true, // Always confetti for submissions!
  },
  bid_won: {
    title: 'You Won!',
    icon: '🏆',
    xoloState: 'celebrate',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950',
    showConfetti: true, // Definitely confetti for wins!
  },
};

/** Milestone configuration */
export interface MilestoneConfig {
  count: number;
  label: string;
  isSpecial: boolean;
}

/** Rate limit configuration (for future use) */
export interface RateLimitConfig {
  /** Max celebrations per session */
  maxPerSession: number;
  /** Cooldown between same type in seconds */
  cooldownSeconds: number;
  /** Enable rate limiting */
  enabled: boolean;
}

/** Default rate limit config (disabled for MVP) */
export const DEFAULT_RATE_LIMIT_CONFIG: RateLimitConfig = {
  maxPerSession: Infinity,
  cooldownSeconds: 0,
  enabled: false,
};
